/**
 * Aware Partner Network — Phase 3 client-side attribution storage.
 *
 * 90-day persistence in localStorage. Refreshed on every partner-link
 * click (the same partner extends the window; a new partner overrides
 * the old one). Forwarded to /api/payments/checkout when the customer
 * returns later through the bare /book-water-test URL.
 *
 * Storage key: `pws_partner_attribution` (canonical — matches the
 * backend constant `PARTNER_ATTRIBUTION_STORAGE_KEY`).
 *
 * Shape:
 *   {
 *     partner_slug: "PWS-NOOSA",
 *     partner_id: "<server-resolved id, never trusted by backend>",
 *     first_click_at: "2026-02-26T01:23:45.000Z",
 *     last_click_at:  "2026-02-26T01:23:45.000Z",
 *     expires_at:     "2026-05-27T01:23:45.000Z",
 *     source_url:     "https://noosa-franchisee.example.com/promo",
 *     utm: { utm_source, utm_medium, utm_campaign, utm_term, utm_content }
 *   }
 *
 * Safety:
 *   • No PII (no name, no email, no phone)
 *   • All reads are defensive — JSON.parse failures + tampered shapes
 *     return null without throwing
 *   • Server re-resolves the slug + re-validates expiry on every
 *     checkout, so a tampered blob can never elevate attribution
 *     to an inactive partner.
 */

export const PARTNER_ATTRIBUTION_KEY = "pws_partner_attribution";
export const PARTNER_ATTRIBUTION_WINDOW_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isoNow() {
  return new Date().toISOString();
}

function isoFromNowDays(days) {
  return new Date(Date.now() + days * MS_PER_DAY).toISOString();
}

function isValidSlugShape(slug) {
  return typeof slug === "string" && /^[A-Z0-9](?:[A-Z0-9]|-(?!-))*[A-Z0-9]$/.test(slug);
}

function readUTMFromSearch(searchParams) {
  if (!searchParams) return {};
  const out = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = searchParams.get(k);
    if (typeof v === "string" && v.trim()) out[k] = v.trim().slice(0, 240);
  });
  return out;
}

/**
 * Read + validate the stored attribution blob.
 * Returns the cleaned blob (incl. canonicalised expiry) or null.
 */
export function readPartnerAttribution() {
  if (!isBrowser()) return null;
  let raw;
  try {
    raw = window.localStorage.getItem(PARTNER_ATTRIBUTION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let blob;
  try {
    blob = JSON.parse(raw);
  } catch {
    // Corrupt JSON — clear it so we don't loop on the next read
    try { window.localStorage.removeItem(PARTNER_ATTRIBUTION_KEY); } catch { /* noop */ }
    return null;
  }
  if (!blob || typeof blob !== "object") return null;
  if (!isValidSlugShape(blob.partner_slug)) {
    try { window.localStorage.removeItem(PARTNER_ATTRIBUTION_KEY); } catch { /* noop */ }
    return null;
  }
  const expiresMs = Date.parse(blob.expires_at || "");
  if (!Number.isFinite(expiresMs) || expiresMs <= Date.now()) {
    // Expired — drop silently
    try { window.localStorage.removeItem(PARTNER_ATTRIBUTION_KEY); } catch { /* noop */ }
    return null;
  }
  return blob;
}

/**
 * Write attribution after a fresh ?p= click. Overrides any previous
 * partner attribution stored under a different slug. If the same slug
 * is being re-clicked, the `first_click_at` is preserved and the
 * `last_click_at` + `expires_at` are bumped forward by 90 days.
 */
const TRACKING_RE = /^[a-z0-9_]{1,32}$/;
/** Whitelist-sanitise a src/cmp tracking value; returns null if unsafe. */
export function sanitizeTrackingValue(v) {
  if (typeof v !== "string") return null;
  const s = v.trim().toLowerCase();
  return TRACKING_RE.test(s) ? s : null;
}

export function writePartnerAttribution({
  partner_slug,
  partner_id = null,
  source_url = null,
  utm = {},
  source = null,
  campaign = null,
}) {
  if (!isBrowser()) return null;
  if (!isValidSlugShape(partner_slug)) return null;
  const now = isoNow();
  const expires = isoFromNowDays(PARTNER_ATTRIBUTION_WINDOW_DAYS);
  const prev = readPartnerAttribution();
  const isSamePartner = prev && prev.partner_slug === partner_slug;
  const src = sanitizeTrackingValue(source);
  const cmp = sanitizeTrackingValue(campaign);
  // First-touch: set once per attribution lifecycle, NEVER overwritten —
  // survives a later different-partner click (Jun 2026 PODAC stage 2).
  const firstTouch = prev?.first_touch || {
    partner_slug,
    partner_id: partner_id || null,
    source: src,
    campaign: cmp,
    seen_at: now,
  };
  const blob = {
    partner_slug,
    partner_id: partner_id || null,
    first_click_at: isSamePartner ? (prev.first_click_at || now) : now,
    last_click_at: now,
    expires_at: expires,
    source_url: source_url || (typeof window !== "undefined" && window.location ? window.location.href.slice(0, 1024) : null),
    utm: (utm && typeof utm === "object" && Object.keys(utm).length ? utm : (prev?.utm || {})) || {},
    source: src,
    campaign: cmp,
    first_touch: firstTouch,
    last_touch: {
      partner_slug,
      source: src,
      campaign: cmp,
      seen_at: now,
    },
  };
  try {
    window.localStorage.setItem(PARTNER_ATTRIBUTION_KEY, JSON.stringify(blob));
  } catch {
    return null;
  }
  return blob;
}

export function clearPartnerAttribution() {
  if (!isBrowser()) return;
  try { window.localStorage.removeItem(PARTNER_ATTRIBUTION_KEY); } catch { /* noop */ }
}

/**
 * Helper for Booking.jsx: snapshot the UTMs from the current URL into
 * the attribution blob on every fresh ?p= click.
 */
export function captureUTMFromURLSearchParams(searchParams) {
  return readUTMFromSearch(searchParams);
}

// Tree-shake friendly default export bundle
const PartnerAttribution = {
  PARTNER_ATTRIBUTION_KEY,
  PARTNER_ATTRIBUTION_WINDOW_DAYS,
  readPartnerAttribution,
  writePartnerAttribution,
  clearPartnerAttribution,
  captureUTMFromURLSearchParams,
};
export default PartnerAttribution;
